import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AiOutlineFilePdf, AiOutlinePrinter } from "react-icons/ai";
import { FiBookOpen, FiRefreshCw } from "react-icons/fi";
import { BsFiletypeXls } from "react-icons/bs";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { Month } from "../../OtherData/Month";
import { getActionTypeLabel } from "../../OtherData/actionTypeMap";
import { errorAlert } from "../../Components/Alerts";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import { AddActionsButton } from "../../Components/AddElementsButton";
import DateInputDdMmYyyy from "../../Components/DateInputDdMmYyyy";
import { salesColumns, salesMappings } from "../../Components/ExcelConverter/ExcelBooksHeader";
import ReusableAccountBooksGrid from "../../Components/ReusableAccountBooksGrid";
import CrudPageHeader from "../../Components/CrudPageHeader";
import "./SalesBookPage.scss";
import "../../Style/__button.scss";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));

function formatDateForDisplay(date) {
  if (!date) {
    return "";
  }

  if (date instanceof Date) {
    if (Number.isNaN(date.getTime())) {
      return "";
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }

  const raw = String(date).trim();
  if (!raw) {
    return "";
  }

  const dottedMatch = raw.match(/^(\d{2})[.](\d{2})[.](\d{4})$/);
  if (dottedMatch) {
    return raw;
  }

  const slashMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (slashMatch) {
    const [, day, month, year] = slashMatch;
    return `${day}.${month}.${year}`;
  }

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${day}.${month}.${year}`;
  }

  const parsedDate = new Date(raw);
  if (Number.isNaN(parsedDate.getTime())) {
    return raw;
  }

  const day = String(parsedDate.getDate()).padStart(2, "0");
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const year = parsedDate.getFullYear();
  return `${day}.${month}.${year}`;
}

export default function ExpenceBookPage() {
  const headers = authHeader();
  const { fiscalYear } = useContext(FiscalYearContext);

  const [data, setData] = useState([]);
  const [month, setMonth] = useState(0);
  const [ngaData, setNgaData] = useState("");
  const [deriData, setDeriData] = useState("");
  const [minDate, setMinDate] = useState("");
  const [maxDate, setMaxDate] = useState("");
  const [companiesData, setCompaniesData] = useState([]);
  const [logoPath, setLogoPath] = useState("");
  const [vatDeclarant, setVatDeclarant] = useState(true);
  const [showMonthFilter, setShowMonthFilter] = useState(true);

  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [quickFilter, setQuickFilter] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [serverTotals, setServerTotals] = useState({
    total: 0,
    amount0: 0,
    amount8: 0,
    vat8: 0,
    amount18: 0,
    vat18: 0,
    totalNoVat: 0,
    totalVat: 0,
  });

  useEffect(() => {
    setMinDate(`${fiscalYear}-01-01`);
    setMaxDate(`${fiscalYear}-12-31`);
  }, [fiscalYear]);

  const openDateFilter = () => {
    setShowMonthFilter(false);
    setMonth(0);
    setData([]);
  };

  const openMonthFilter = () => {
    setShowMonthFilter(true);
    setNgaData("");
    setDeriData("");
    setData([]);
  };

  const fetchCompaniesData = async () => {
    try {
      const response = await axios.get(
        ApiLink.API_URL + "Companies/getCompanyDetails",
        { headers },
      );
      setCompaniesData(response.data);
      setLogoPath(ApiLink.PHOTO_URL + response.data[0].CompanyLogo);
      setVatDeclarant(response.data[0].VATDeclarant);
    } catch (error) {
      errorAlert("Gabim në kërkesë:", error);
      if (
        error.response
        && error.response.data
        && error.response.data.errorMessage
      ) {
        errorAlert(
          `Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`,
        );
      } else if (error.message) {
        errorAlert(`Gabim: ${error.message}`);
      } else {
        errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
      }
    }
  };

  const buildPeriodParams = () => {
    const params = {};

    if (month && month >= 1 && month <= 12) {
      params.month = month;
      params.fiscalYear = fiscalYear;
    } else if (ngaData && deriData) {
      params.firstDate = ngaData;
      params.secondDate = deriData;
    } else {
      return null;
    }

    return params;
  };

  const fetchData = async () => {
    const periodParams = buildPeriodParams();
    if (!periodParams) {
      setData([]);
      setRowCount(0);
      setServerTotals({
        total: 0,
        amount0: 0,
        amount8: 0,
        vat8: 0,
        amount18: 0,
        vat18: 0,
        totalNoVat: 0,
        totalVat: 0,
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        ApiLink.API_URL + "AccountingBooks/expencebook",
        {
          headers,
          params: {
            ...periodParams,
            page: paginationModel.page,
            pageSize: paginationModel.pageSize,
            sort: JSON.stringify(sortModel),
            filter: JSON.stringify(filterModel.items || []),
            quickFilter: quickFilter.join(" "),
          },
        },
      );

      const payload = response?.data;
      const items = Array.isArray(payload)
        ? payload
        : (payload?.items || payload?.data || []);
      const total = Array.isArray(payload)
        ? items.length
        : Number(payload?.total ?? payload?.totalRows ?? items.length);
      const payloadTotals = Array.isArray(payload)
        ? null
        : payload?.totals;

      setData(items);
      setRowCount(total);
      if (payloadTotals) {
        const totalValue = Number(payloadTotals.total || 0);
        const amount0 = Number(payloadTotals.amount0 || 0);
        const amount8 = Number(payloadTotals.amount8 || 0);
        const vat8 = Number(payloadTotals.vat8 || 0);
        const amount18 = Number(payloadTotals.amount18 || 0);
        const vat18 = Number(payloadTotals.vat18 || 0);

        setServerTotals({
          total: totalValue,
          amount0,
          amount8,
          vat8,
          amount18,
          vat18,
          totalNoVat: totalValue - (vat8 + vat18),
          totalVat: vat8 + vat18,
        });
      } else {
        const fallbackTotals = items.reduce((acc, item) => {
          const totalValue = Number(item.Total || 0);
          const amount0 = Number(item.Amount0 || 0);
          const amount8 = Number(item.Amount8 || 0);
          const vat8 = Number(item.VAT8 || 0);
          const amount18 = Number(item.Amount18 || 0);
          const vat18 = Number(item.VAT18 || 0);

          return {
            total: acc.total + totalValue,
            amount0: acc.amount0 + amount0,
            amount8: acc.amount8 + amount8,
            vat8: acc.vat8 + vat8,
            amount18: acc.amount18 + amount18,
            vat18: acc.vat18 + vat18,
            totalNoVat: acc.totalNoVat + (totalValue - (vat8 + vat18)),
            totalVat: acc.totalVat + (vat8 + vat18),
          };
        }, {
          total: 0,
          amount0: 0,
          amount8: 0,
          vat8: 0,
          amount18: 0,
          vat18: 0,
          totalNoVat: 0,
          totalVat: 0,
        });

        setServerTotals(fallbackTotals);
      }
    } catch (error) {
      if (error.response && error.response.status === 400) {
        return;
      }

      if (error.response?.data?.errorMessage) {
        errorAlert(`Gabim: ${error.response.data.errorMessage}`);
      } else if (error.message) {
        errorAlert(`Gabim: ${error.message}`);
      } else {
        errorAlert("Gabim i panjohur në kërkesë.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [month, fiscalYear, ngaData, deriData, paginationModel, sortModel, filterModel, quickFilter]);

  useEffect(() => {
    if ((month && fiscalYear) || (ngaData && deriData)) {
      fetchCompaniesData();
    }
  }, [month, fiscalYear, ngaData, deriData]);

  const totals = useMemo(() => serverTotals, [serverTotals]);

  const gridColumns = useMemo(() => {
    const baseColumns = [
      { field: "rowNumber", headerName: "#", minWidth: 70, width: 80 },
      { field: "Lloji", headerName: "Lloji", minWidth: 120, width: 130 },
      { field: "Date", headerName: "Data", minWidth: 120, width: 130 },
      { field: "InvoiceNr", headerName: "Numri Faturës", minWidth: 180, width: 200 },
      { field: "Tipi", headerName: "Tipi", minWidth: 120, width: 130 },
      { field: "AccountName", headerName: "Emri i Furnitorit", minWidth: 220, width: 250 },
      { field: "IdentificationNumber", headerName: "Numri Identifikues", minWidth: 180, width: 200 },
      { field: "VATNumber", headerName: "Numri i TVSH-së", minWidth: 170, width: 190 },
      {
        field: "Total",
        headerName: "Total",
        minWidth: 140,
        width: 160,
        valueFormatter: (value) => formatCurrency(value),
      },
    ];

    if (!vatDeclarant) {
      return baseColumns;
    }

    return [
      ...baseColumns,
      {
        field: "Amount0",
        headerName: "Vlera 0%",
        minWidth: 140,
        width: 150,
        valueFormatter: (value) => formatCurrency(value),
      },
      {
        field: "Amount8",
        headerName: "Vlera 8%",
        minWidth: 140,
        width: 150,
        valueFormatter: (value) => formatCurrency(value),
      },
      {
        field: "VAT8",
        headerName: "TVSH 8%",
        minWidth: 130,
        width: 140,
        valueFormatter: (value) => formatCurrency(value),
      },
      {
        field: "Amount18",
        headerName: "Vlera 18%",
        minWidth: 140,
        width: 150,
        valueFormatter: (value) => formatCurrency(value),
      },
      {
        field: "VAT18",
        headerName: "TVSH 18%",
        minWidth: 130,
        width: 140,
        valueFormatter: (value) => formatCurrency(value),
      },
    ];
  }, [vatDeclarant]);

  const rows = useMemo(() => (
    data.map((item, index) => ({
      id: item.TransactionID || `${item.InvoiceNr || "expence"}-${index}`,
      rowNumber: item.Nr || (paginationModel.page * paginationModel.pageSize + index + 1),
      Lloji: item.TransactionType,
      Date: formatDateForDisplay(item.Date),
      InvoiceNr: item.InvoiceNr,
      Tipi: getActionTypeLabel(item.ActionType),
      AccountName: item.AccountName,
      IdentificationNumber: item.IdentificationNumber,
      VATNumber: item.VATNumber,
      Total: item.Total,
      Amount0: item.Amount0,
      Amount8: item.Amount8,
      VAT8: item.VAT8,
      Amount18: item.Amount18,
      VAT18: item.VAT18,
      rowInfo: item,
    }))
  ), [data, paginationModel.page, paginationModel.pageSize]);

  const summaryFooter = useMemo(() => {
    if (vatDeclarant) {
      return {
        labelField: "VATNumber",
        label: "Totali",
        values: {
          Total: formatCurrency(totals.total),
          Amount0: formatCurrency(totals.amount0),
          Amount8: formatCurrency(totals.amount8),
          VAT8: formatCurrency(totals.vat8),
          Amount18: formatCurrency(totals.amount18),
          VAT18: formatCurrency(totals.vat18),
        },
      };
    }

    return {
      labelField: "VATNumber",
      label: "Totali",
      values: {
        Total: formatCurrency(totals.total),
      },
    };
  }, [totals, vatDeclarant]);

  const reportPeriodLabel = useMemo(() => {
    if (month && month >= 1 && month <= 12) {
      const monthMatch = Month.find((item) => Number(item.id) === Number(month));
      const monthLabel = monthMatch?.month || `Muaji ${month}`;
      return `${monthLabel} ${fiscalYear}`;
    }

    if (ngaData && deriData) {
      return `${formatDateForDisplay(ngaData)} - ${formatDateForDisplay(deriData)}`;
    }

    return "Pa periudhë të zgjedhur";
  }, [month, fiscalYear, ngaData, deriData]);

  const addLogo = (doc) => {
    if (!logoPath) {
      return;
    }

    const logoWidth = 24;
    const logoHeight = 24;
    const logoX = 12;
    const logoY = 10;
    doc.addImage(logoPath, "JPEG", logoX, logoY, logoWidth, logoHeight);
  };

  const addReportHeader = (doc, companyData, periodLabel) => {
    const pageWidth = doc.internal.pageSize.getWidth();

    addLogo(doc);

    doc.setDrawColor(40, 73, 69);
    doc.setLineWidth(0.5);
    doc.line(12, 38, pageWidth - 12, 38);

    doc.setTextColor(31, 58, 54);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Libri i Shpenzimeve", 40, 18);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(95, 127, 115);
    doc.text("Raport financiar i shpenzimeve", 40, 24);

    doc.setFontSize(9);
    doc.setTextColor(31, 58, 54);
    doc.text(`Periudha: ${periodLabel}`, 12, 46);

    if (companyData) {
      const companyDetails = [
        companyData.CompanyName,
        `Numri Identifikues: ${companyData.IdentificationNumber || "-"}`,
        `Numri i TVSH-së: ${companyData.VATNumber || "-"}`,
        `Adresa: ${companyData.Address || "-"}`,
        `Tel: ${companyData.PhoneNumbers || "-"}`,
        `Email: ${companyData.Email || "-"}`,
      ];

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text(companyDetails[0], pageWidth - 12, 16, { align: "right" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(95, 127, 115);
      doc.text(companyDetails.slice(1), pageWidth - 12, 22, { align: "right" });
    }

    return 52;
  };

  const addReportFooter = (doc, periodLabel, generatedOnLabel) => {
    const pageCount = doc.getNumberOfPages();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const brandingLabel = "© www.accountree.net";
    const footerCenterLabel = `Gjeneruar më: ${generatedOnLabel} | ${brandingLabel}`;

    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      doc.setDrawColor(212, 226, 219);
      doc.setLineWidth(0.3);
      doc.line(12, pageHeight - 10, pageWidth - 12, pageHeight - 10);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(95, 127, 115);
      doc.text(`Periudha: ${periodLabel}`, 12, pageHeight - 5.5);
      doc.text(footerCenterLabel, pageWidth / 2, pageHeight - 5.5, { align: "center" });
      doc.text(`Faqe ${page} / ${pageCount}`, pageWidth - 12, pageHeight - 5.5, { align: "right" });
    }
  };

  const addTable = (doc, dataRows, startY) => {
    const headersRow = vatDeclarant
      ? [
        "#",
        "Lloji",
        "Data",
        "Numri Faturës",
        "Emri i Furnitorit",
        "Numri Identifikues",
        "Numri i TVSH-së",
        "Total",
        "Vlera 0%",
        "Vlera 8%",
        "TVSH 8%",
        "Vlera 18%",
        "TVSH 18%",
      ]
      : [
        "#",
        "Lloji",
        "Data",
        "Numri Faturës",
        "Emri i Furnitorit",
        "Numri Identifikues",
        "Numri i TVSH-së",
        "Total",
      ];

    const bodyRows = dataRows.map((item, index) => {
      const baseRow = [
        index + 1,
        item.TransactionType || "-",
        formatDateForDisplay(item.Date),
        item.InvoiceNr || "-",
        item.AccountName || "-",
        item.IdentificationNumber || "-",
        item.VATNumber || "-",
        formatCurrency(item.Total),
      ];

      if (!vatDeclarant) {
        return baseRow;
      }

      return [
        ...baseRow,
        formatCurrency(item.Amount0),
        formatCurrency(item.Amount8),
        formatCurrency(item.VAT8),
        formatCurrency(item.Amount18),
        formatCurrency(item.VAT18),
      ];
    });

    const footRow = vatDeclarant
      ? [
        "",
        "",
        "",
        "",
        "",
        "",
        "Totali",
        formatCurrency(totals.total),
        formatCurrency(totals.amount0),
        formatCurrency(totals.amount8),
        formatCurrency(totals.vat8),
        formatCurrency(totals.amount18),
        formatCurrency(totals.vat18),
      ]
      : [
        "",
        "",
        "",
        "",
        "",
        "",
        "Totali",
        formatCurrency(totals.total),
      ];

    const baseColumnStyles = vatDeclarant
      ? {
        0: { cellWidth: 8, halign: "center" },
        1: { cellWidth: 18 },
        2: { cellWidth: 18, halign: "center" },
        3: { cellWidth: 25 },
        4: { cellWidth: 36 },
        5: { cellWidth: 28 },
        6: { cellWidth: 24 },
        7: { cellWidth: 20, halign: "right" },
        8: { cellWidth: 18, halign: "right" },
        9: { cellWidth: 18, halign: "right" },
        10: { cellWidth: 18, halign: "right" },
        11: { cellWidth: 18, halign: "right" },
        12: { cellWidth: 18, halign: "right" },
      }
      : {
        0: { cellWidth: 10, halign: "center" },
        1: { cellWidth: 22 },
        2: { cellWidth: 22, halign: "center" },
        3: { cellWidth: 30 },
        4: { cellWidth: 54 },
        5: { cellWidth: 36 },
        6: { cellWidth: 34 },
        7: { cellWidth: 24, halign: "right" },
      };

    const horizontalMargin = 12;
    const availableTableWidth = doc.internal.pageSize.getWidth() - (horizontalMargin * 2);
    const baseTableWidth = Object.values(baseColumnStyles).reduce(
      (sum, style) => sum + Number(style.cellWidth || 0),
      0,
    );
    const scaleRatio = baseTableWidth > 0 ? availableTableWidth / baseTableWidth : 1;
    const columnStyles = Object.entries(baseColumnStyles).reduce((acc, [key, style]) => {
      acc[key] = {
        ...style,
        cellWidth: Number((Number(style.cellWidth || 0) * scaleRatio).toFixed(3)),
      };
      return acc;
    }, {});

    doc.autoTable({
      head: [headersRow],
      body: bodyRows,
      foot: [footRow],
      startY,
      showHead: "firstPage",
      showFoot: "lastPage",
      margin: {
        top: 16,
        left: horizontalMargin,
        right: horizontalMargin,
        bottom: 16,
      },
      theme: "grid",
      styles: {
        fontSize: 7.5,
        cellPadding: { top: 1.5, right: 1.5, bottom: 1.5, left: 1.5 },
        lineColor: [212, 226, 219],
        lineWidth: 0.1,
        textColor: [31, 58, 54],
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [40, 73, 69],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
        valign: "middle",
      },
      bodyStyles: {
        valign: "middle",
      },
      footStyles: {
        fillColor: [231, 240, 235],
        textColor: [31, 58, 54],
        fontStyle: "bold",
      },
      didParseCell: (hookData) => {
        if (hookData.section !== "foot") {
          return;
        }

        if (hookData.column.index >= 7) {
          hookData.cell.styles.halign = "right";
        }
      },
      alternateRowStyles: {
        fillColor: [247, 250, 248],
      },
      columnStyles,
    });
  };

  const fetchAllRowsForExport = async () => {
    const periodParams = buildPeriodParams();
    if (!periodParams) {
      return [];
    }

    const totalRowsToExport = Math.max(Number(rowCount) || 0, 1);
    const response = await axios.get(
      ApiLink.API_URL + "AccountingBooks/expencebook",
      {
        headers,
        params: {
          ...periodParams,
          page: 0,
          pageSize: totalRowsToExport,
          sort: JSON.stringify(sortModel),
          filter: JSON.stringify(filterModel.items || []),
          quickFilter: quickFilter.join(" "),
        },
      },
    );

    const payload = response?.data;
    if (Array.isArray(payload)) {
      return payload;
    }

    return payload?.items || payload?.data || [];
  };

  const buildPdfDocument = async () => {
    const exportRows = await fetchAllRowsForExport();

    if (!exportRows.length) {
      errorAlert("Nuk ka të dhëna për eksport në PDF.");
      return null;
    }

    const doc = new jsPDF({ orientation: "landscape" });
    const generatedOnLabel = formatDateForDisplay(new Date());
    const startY = addReportHeader(doc, companiesData[0], reportPeriodLabel);
    const companyNameRaw = (companiesData?.[0]?.CompanyName || "Kompania").trim();
    const companyNameForFile = companyNameRaw
      .replace(/[\\/:*?"<>|]/g, "")
      .replace(/\s+/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "") || "Kompania";

    addTable(doc, exportRows, startY);
    addReportFooter(doc, reportPeriodLabel, generatedOnLabel);

    return { doc, companyNameForFile };
  };

  const buildPdfFileName = (companyNameForFile) => {
    if (month === 0) {
      return `LShpenzimeve_Periudha_${formatDateForDisplay(ngaData)}_deri_${formatDateForDisplay(deriData)}_${companyNameForFile}.pdf`;
    }

    return `LShpenzimeve_Muaji_${fiscalYear}-${month}_${companyNameForFile}.pdf`;
  };

  const handleExportPDF = async () => {
    const generatedDocument = await buildPdfDocument();
    if (!generatedDocument) {
      return;
    }

    const { doc, companyNameForFile } = generatedDocument;
    doc.save(buildPdfFileName(companyNameForFile));
  };

  const handlePrintPDF = async () => {
    const generatedDocument = await buildPdfDocument();
    if (!generatedDocument) {
      return;
    }

    const { doc } = generatedDocument;
    const pdfBlob = doc.output("blob");
    const printUrl = URL.createObjectURL(pdfBlob);
    const printFrame = document.createElement("iframe");
    printFrame.style.position = "fixed";
    printFrame.style.left = "-10000px";
    printFrame.style.top = "0";
    printFrame.style.width = "1024px";
    printFrame.style.height = "768px";
    printFrame.style.border = "0";
    printFrame.style.opacity = "0";
    printFrame.style.pointerEvents = "none";
    let didPrint = false;
    let cleanupDone = false;

    const cleanup = () => {
      if (cleanupDone) {
        return;
      }

      cleanupDone = true;
      URL.revokeObjectURL(printUrl);
      if (printFrame.parentNode) {
        printFrame.parentNode.removeChild(printFrame);
      }
    };

    printFrame.onload = () => {
      if (didPrint) {
        return;
      }

      didPrint = true;

      try {
        const frameWindow = printFrame.contentWindow;
        if (!frameWindow) {
          cleanup();
          errorAlert("Printimi nuk u inicializua. Ju lutem provoni përsëri.");
          return;
        }

        window.setTimeout(() => {
          frameWindow.focus();
          frameWindow.onafterprint = cleanup;
          frameWindow.print();
          window.setTimeout(cleanup, 5000);
        }, 450);
      } catch (error) {
        cleanup();
        errorAlert("Nuk u hap dialogu i printimit. Ju lutem provoni përsëri.");
      }
    };

    document.body.appendChild(printFrame);
    printFrame.src = printUrl;

    window.setTimeout(() => {
      cleanup();
    }, 60_000);
  };

  const xlsName = month === 0
    ? `LShpenzimeve_Periudha_${formatDateForDisplay(ngaData)}_deri_${formatDateForDisplay(deriData)}`
    : `LShpenzimeve_Muaji_${fiscalYear}-${month}`;

  const filterToolbar = (
    <>
      <button
        type="button"
        className="RSDG__toolButton SBP__toolbarSwitch"
        onClick={() => {
          if (showMonthFilter) {
            openDateFilter();
          } else {
            openMonthFilter();
          }
        }}
        aria-label="Ndrysho mënyrën e filtrimit"
        title={`Ndrysho filtrin: ${showMonthFilter ? "Muaji" : "Datat"}`}
      >
        <span className="RSDG__toolButtonContent">
          <FiRefreshCw size={14} className={`SBP__toolbarSwitchIcon${showMonthFilter ? "" : " SBP__toolbarSwitchIcon--rotated"}`} />
          <span>{showMonthFilter ? "Muaji" : "Datat"}</span>
        </span>
      </button>

      {showMonthFilter ? (
        <TextField
          className="SBP__toolbarField SBP__toolbarField--month"
          select
          size="small"
          label="Muaji"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          required
        >
          {Month.map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {fiscalYear} {item.month}
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <>
          <div className="SBP__toolbarDateWrap SBP__toolbarDateWrap--date">
            <DateInputDdMmYyyy
              label="Nga"
              value={ngaData}
              minDate={minDate}
              maxDate={maxDate}
              onChange={setNgaData}
              fullWidth={false}
            />
          </div>
          <div className="SBP__toolbarDateWrap SBP__toolbarDateWrap--date">
            <DateInputDdMmYyyy
              label="Deri"
              value={deriData}
              minDate={minDate}
              maxDate={maxDate}
              onChange={setDeriData}
              fullWidth={false}
            />
          </div>
        </>
      )}
    </>
  );

  const fetchExportRows = async () => {
    const exportRows = await fetchAllRowsForExport();

    return exportRows.map((item, index) => ({
      id: item.TransactionID || `${item.InvoiceNr || "expence"}-${index}`,
      rowNumber: index + 1,
      Lloji: item.TransactionType,
      Date: formatDateForDisplay(item.Date),
      InvoiceNr: item.InvoiceNr,
      Tipi: getActionTypeLabel(item.ActionType),
      AccountName: item.AccountName,
      IdentificationNumber: item.IdentificationNumber,
      VATNumber: item.VATNumber,
      Total: item.Total,
      Amount0: item.Amount0,
      Amount8: item.Amount8,
      VAT8: item.VAT8,
      Amount18: item.Amount18,
      VAT18: item.VAT18,
      rowInfo: item,
    }));
  };

  const exportDatasetToExcel = (items, fileName) => {
    if (!items || items.length === 0) {
      errorAlert("Nuk ka të dhëna për eksport në Excel.");
      return;
    }

    const filteredData = items.map((item) => (
      salesColumns.reduce((accumulator, column) => {
        accumulator[salesMappings[column] || column] = item[column];
        return accumulator;
      }, {})
    ));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const excelFile = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(excelFile, `${fileName}.xlsx`);
  };

  const handleExportXlsx = async () => {
    const exportRows = await fetchAllRowsForExport();
    exportDatasetToExcel(exportRows, xlsName);
  };

  const hasSelectedPeriod = (month && month >= 1 && month <= 12) || (ngaData && deriData);
  const emptyGridMessage = hasSelectedPeriod
    ? "Nuk ka te dhena."
    : "Ju lutem zgjidhni periudhen (muajin ose datat).";

  return (
    <div className="Crud__Container SBP__page">
      <div className="SBP__content">
        <CrudPageHeader
          icon={FiBookOpen}
          title="Libri i Shpenzimeve"
          description="Forma e librit të shpenzimeve: menaxhim dhe eksport i të dhënave sipas muajit ose intervalit të datave."
          className="SBP__header"
        />

        <div className="SBP__reportGrid">
          <ReusableAccountBooksGrid
            columns={gridColumns}
            rows={rows}
            loading={loading}
            rowCount={rowCount}
            height={650}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            sortModel={sortModel}
            setSortModel={setSortModel}
            filterModel={filterModel}
            setFilterModel={setFilterModel}
            quickFilter={quickFilter}
            setQuickFilter={setQuickFilter}
            showQuickSearch={false}
            getExportRows={fetchExportRows}
            showExportButton={false}
            toolbarActions={(
              <>
                <AddActionsButton
                  text="Print"
                  onClick={handlePrintPDF}
                  icon={<AiOutlinePrinter />}
                  className="RSDG__toolButton SBP__gridActionBtn--print"
                />
                <AddActionsButton
                  text="PDF"
                  onClick={handleExportPDF}
                  icon={<AiOutlineFilePdf />}
                  className="RSDG__toolButton SBP__gridActionBtn--pdf"
                />
                <AddActionsButton
                  text="XLSX"
                  onClick={handleExportXlsx}
                  icon={<BsFiletypeXls />}
                  className="RSDG__toolButton SBP__gridActionBtn--xlsx"
                />
              </>
            )}
            toolbarRightActions={filterToolbar}
            showViewAction={false}
            showEditAction={false}
            showDeleteAction={false}
            emptyMessage={emptyGridMessage}
            summaryFooter={summaryFooter}
          />
        </div>
      </div>
    </div>
  );
}
